/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import type { MapMarkerItem } from './map.types';

// ~20 feet expressed in degrees of latitude. One degree of latitude is
// roughly 364,000 feet, so 20 / 364,000 ≈ 0.000055°. Used to nudge markers
// that share identical coordinates so they don't render directly on top of
// one another.
const OFFSET_FEET = 20;
const FEET_PER_DEGREE_LAT = 364000;
const OFFSET_DEGREES = OFFSET_FEET / FEET_PER_DEGREE_LAT;

/**
 * A marker whose coordinates have been validated as usable strings.
 *
 * Because `MapMarkerItem` already types `latitude`/`longitude` as `string`,
 * this intersection is structurally identical to `T`. It is kept explicit so
 * the contract ("returns items with guaranteed lat/lng strings") is legible
 * at the call site and survives any future widening of `MapMarkerItem`.
 */
export type ValidatedMarkerItem<T extends MapMarkerItem> = T & {
    latitude: string;
    longitude: string;
};

/**
 * Offsets items that share identical coordinates so their map markers don't
 * overlap. The first item in each duplicate group keeps its original
 * position; the rest are distributed evenly around a small circle (~20 feet).
 */
function offsetDuplicateCoordinates<T extends MapMarkerItem>(
    items: ReadonlyArray<ValidatedMarkerItem<T>>
): Array<ValidatedMarkerItem<T>> {
    // Group items by their coordinate key.
    const coordGroups = new Map<string, Array<ValidatedMarkerItem<T>>>();
    items.forEach((item) => {
        const key = `${item.latitude},${item.longitude}`;
        const group = coordGroups.get(key) ?? [];
        group.push(item);
        coordGroups.set(key, group);
    });

    const result: Array<ValidatedMarkerItem<T>> = [];
    coordGroups.forEach((group) => {
        const [first, ...rest] = group;
        if (!first) return;

        // First item stays at its original position.
        result.push(first);

        // Distribute the remaining duplicates around a circle so they fan out
        // from the shared point.
        rest.forEach((item, index) => {
            const angle = ((2 * Math.PI) / rest.length) * index;
            const latOffset = OFFSET_DEGREES * Math.cos(angle);
            const lngOffset = OFFSET_DEGREES * Math.sin(angle);
            result.push({
                ...item,
                latitude: (parseFloat(item.latitude) + latOffset).toString(),
                longitude: (parseFloat(item.longitude) + lngOffset).toString(),
            });
        });
    });

    return result;
}

/**
 * Filters out markers with unusable coordinates and de-clusters those that
 * share an identical position.
 *
 * An item is dropped when either coordinate is missing, an empty string, or
 * exactly `'0'` (the API's sentinel for "no location"). Surviving items that
 * land on the same point are offset in a circular pattern so every marker is
 * individually visible and clickable.
 *
 * Memoized on `data`, so the returned array is referentially stable until the
 * input changes — safe to pass straight into `useMapMarkers`.
 */
export function useValidatedData<T extends MapMarkerItem>(
    data: ReadonlyArray<T> | undefined
): Array<ValidatedMarkerItem<T>> {
    return React.useMemo(() => {
        if (!data || !Array.isArray(data)) return [];

        const validItems = data.filter(
            (item): item is ValidatedMarkerItem<T> =>
                Boolean(item.latitude) &&
                Boolean(item.longitude) &&
                item.latitude !== '0' &&
                item.longitude !== '0'
        );

        return offsetDuplicateCoordinates(validItems);
    }, [data]);
}
